import React, { Component } from 'react';
import Style from './index.less';
import classNames from 'classnames';
import i18n from '@i18next/i18n';
import { IPictureModelProps } from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/picture-model-props.interface';
import omit from 'lodash/omit';
import ImageFilter from '@src/framework/editor/src/page/index/components/canvas/images-filter';
import RecolorPresetMenu from '../../recolor-preset-menu';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import Tooltip from '@tencent/dui/lib/components/Tooltip';
import { IElementModelProps } from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/element-model-props.interface';
interface PresetMenuProps {
    selectedElementModel: IPictureModel;
    pictureProps: IPictureModelProps;
    onPresetStyleClick: (value) => void;
    onPresetStyleMouseEnter?: (value) => void;
    onPresetStyleMouseLeave?: () => void;
    displaySaturate?: boolean;
    displayColorTemperature?: boolean;
    displayRecolor?: boolean;
}

interface IPictureColorPresetItem {
    key: string;
    title: string;
    saturate?: number;
    colorTemperature?: number;
}

const SaturateStyleList: IPictureColorPresetItem[] = [
    {
        key: 'saturate-0',
        saturate: 0,
        title: i18n.t('饱和度{0}%', { 0: 0 }),
    },
    {
        key: 'saturate-33',
        saturate: 33,
        title: i18n.t('饱和度{0}%', { 0: 33 }),
    },
    {
        key: 'saturate-66',
        saturate: 66,
        title: i18n.t('饱和度{0}%', { 0: 66 }),
    },
    {
        key: 'saturate-100',
        saturate: 100,
        title: i18n.t('饱和度{0}%', { 0: 100 }),
    },
    {
        key: 'saturate-200',
        saturate: 200,
        title: i18n.t('饱和度{0}%', { 0: 200 }),
    },
    {
        key: 'saturate-300',
        saturate: 300,
        title: i18n.t('饱和度{0}%', { 0: 300 }),
    },
    {
        key: 'saturate-400',
        saturate: 400,
        title: i18n.t('饱和度{0}%', { 0: 400 }),
    },
];

const ColorTemperatureStyleList: IPictureColorPresetItem[] = [
    {
        key: 'colorTemperature-4700',
        colorTemperature: 4700,
        title: i18n.t('色温{0}K', { 0: 4700 }),
    },
    {
        key: 'colorTemperature-5300',
        colorTemperature: 5300,
        title: i18n.t('色温{0}K', { 0: 5300 }),
    },
    {
        key: 'colorTemperature-5900',
        colorTemperature: 5900,
        title: i18n.t('色温{0}K', { 0: 5900 }),
    },
    {
        key: 'colorTemperature-6500',
        colorTemperature: 6500,
        title: i18n.t('色温{0}K', { 0: 6500 }),
    },
    {
        key: 'colorTemperature-7200',
        colorTemperature: 7200,
        title: i18n.t('色温{0}K', { 0: 7200 }),
    },
    {
        key: 'colorTemperature-8800',
        colorTemperature: 8800,
        title: i18n.t('色温{0}K', { 0: 8800 }),
    },
    {
        key: 'colorTemperature-11200',
        colorTemperature: 11200,
        title: i18n.t('色温{0}K', { 0: 11200 }),
    },
];


export default class PictureColorPresetMenu extends Component<PresetMenuProps> {
    private imageFilter: ImageFilter;

    public constructor(props) {
        super(props);
        this.imageFilter = new ImageFilter();
    }

    public render() {
        const { displaySaturate = true, displayColorTemperature = true, displayRecolor = true } = this.props;
        return (
            <div className={Style.container}>
                {
                    displaySaturate
                        ? <><div className={Style['preset-type-title']}>{i18n.t('颜色饱和度')}</div>
                            <div className={Style['preset-collection']}>
                                {SaturateStyleList.map(item => this.renderPresetIcon(item))}
                            </div>
                        </> : null
                }
                {
                    displayColorTemperature
                        ? <>
                            <div className={Style['preset-type-title']}>{i18n.t('色调')}</div>
                            <div className={Style['preset-collection']}>
                                {ColorTemperatureStyleList.map(item => this.renderPresetIcon(item))}
                            </div>
                        </> : null
                }
                {
                    displayRecolor
                        ? <>
                            <div className={Style['preset-type-title']}>{i18n.t('重新着色')}</div>
                            <RecolorPresetMenu
                                model={this.props.selectedElementModel}
                                onRecolorStyleClick={this.handlePresetIconClick}
                                fromToolbar={true}
                            />
                        </> : null
                }
            </div>
        );
    }

    private renderPresetIcon(item: IPictureColorPresetItem) {
        const src = this.getSrc(this.props.pictureProps);
        const isSelected = this.isSelected(item);
        const softEdgeItemClass = classNames({
            [Style['preset-collection-item']]: true,
            [Style['preset-item-selected']]: isSelected,
        });
        const { imageFilters, imageFiltersId } = this.defineImageFilter(item);
        return (
            <div
                key={item.key}
                className={softEdgeItemClass}
                onClick={() => this.handlePresetIconClick(item)}
                onMouseEnter={() => this.handlePresetIconMouseEnter(item)}
                onMouseLeave={() => this.handlePresetIconMouseLeave()}
            >
                <Tooltip placement={['top', 'right']} title={i18n.t(item.title)}>
                    <div className={Style['preset-icon']}>
                        { imageFilters }
                        <img src={src} style={{ filter: `url(#${imageFiltersId}) saturate(${(item.saturate ?? 100) / 200})` }}/>
                    </div>
                </Tooltip>
            </div>
        );
    }

    private isSelected(item) {
        const value = this.props.pictureProps.style.image || {};
        return !Object.keys(item).some(key => typeof value[key] !== 'undefined' && item[key] !== value[key]);
    }

    private defineImageFilter = (item) => {
        const { colorTemperature, key } = item;
        const filterId = `image-filter-${key}`;

        const imageFilters = (
            <svg xmlns="http://www.w3.org/2000/svg" version="1.1" style={{ display: 'none' }}>
                <defs>
                    <filter id={filterId} filterUnits="objectBoundingBox" x="0%" y="0%" width="100%" height="100%">
                        { this.imageFilter.renderTemperature(colorTemperature) }
                    </filter>
                </defs>
            </svg>
        );

        return { imageFilters, imageFiltersId: filterId };
    };

    private handlePresetIconMouseEnter = (item: IPictureColorPresetItem) => {
        this.props.onPresetStyleMouseEnter?.(omit(item, ['key', 'title']));
    };

    private handlePresetIconClick = (item: IPictureColorPresetItem) => {
        this.props.onPresetStyleClick?.(omit(item, ['key', 'title']));
    };

    private handlePresetIconMouseLeave = () => {
        this.props.onPresetStyleMouseLeave?.();
    };

    private getSrc = (props: IElementModelProps | IPictureModelProps) => {
        if ('src' in props) {
            return props.src;
        }
        return props.style?.fillBackground?.image?.src;
    };
}
